import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { TOOLBAR_OVERFLOW_TOLERANCE_PX } from './constants';

type UseToolbarCollapseOptions = {
  toolbarRef: RefObject<HTMLDivElement | null>;
  currentPage: number;
  isHorizontal: boolean;
  isPagesOpen: boolean;
  numPages: number;
  scalePct: number;
};

/**
 * Tracks whether the PDF toolbar has enough horizontal space for all actions.
 *
 * When the expanded toolbar overflows, the hook collapses its secondary
 * actions into the overflow menu. It remembers the expanded width and restores
 * the actions once the toolbar grows large enough again. Measurements run when
 * the toolbar resizes or its width-dependent content changes.
 */
export function useToolbarCollapse({
  toolbarRef,
  currentPage,
  isHorizontal,
  isPagesOpen,
  numPages,
  scalePct,
}: UseToolbarCollapseOptions) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const naturalWidthRef = useRef(0);

  const measure = useCallback(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) {
      return;
    }
    if (!isCollapsed) {
      naturalWidthRef.current = toolbar.scrollWidth;
      if (
        toolbar.scrollWidth >
        toolbar.clientWidth + TOOLBAR_OVERFLOW_TOLERANCE_PX
      ) {
        setIsCollapsed(true);
      }
    } else if (toolbar.clientWidth >= naturalWidthRef.current) {
      setIsCollapsed(false);
    }
  }, [isCollapsed, toolbarRef]);

  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) {
      return;
    }
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [measure, toolbarRef]);

  useEffect(() => {
    measure();
  }, [currentPage, isHorizontal, isPagesOpen, measure, numPages, scalePct]);

  return isCollapsed;
}
